import {
  getVenuaPlannerDestinationSuggestions,
  getVenuaPlannerDestinationSeasonOptions,
  getGetVenuaPlannerDestinationSeasonOptionsQueryKey,
} from '@/api/generated/venua-planner-planning/venua-planner-planning';
import type { DestinationSuggestion, ListRequest } from '@/api/generated/schemas';
import {
  defineEntityAsyncSelector,
  listRequestQueryKey,
  selectorPresets,
  toListRequest,
} from '@/shared/ui/async-selector';
import { createAsyncSelectorField } from '@/shared/forms';
import type { DestinationRef } from '../plannerApi';

type DestinationFilters = { seasonId: number | null };

const destinationSelector = defineEntityAsyncSelector<
  DestinationRef,
  DestinationSuggestion,
  ListRequest,
  DestinationFilters
>({
  api: {
    fetch: getVenuaPlannerDestinationSuggestions,
    buildApiParams: (ctx) =>
      toListRequest(
        ctx.search,
        ctx.ids,
        ctx.filters.seasonId != null
          ? [{ field: 'season', ids: [String(ctx.filters.seasonId)] }]
          : []
      ),
    queryKey: (request) => listRequestQueryKey('venua-planner.destinations', request),
    mapItem: (s) => ({
      destinationId: s.destinationId,
      name: s.name,
      code: s.code ?? null,
    }),
  },
  key: (item) => item.destinationId,
  objectCategory: 'Destination',
  // Tři tečky (⋯) otevřou list page dané ObjectCategory v Magic desktopu přes `form/list`,
  // ne interní React pick modal.
  listPicker: 'legacy',
  display: {
    kind: 'nameCode',
    name: (item) => item.name,
    code: (item) => item.code,
  },

  columns: (t) =>
    selectorPresets.nameAndCode({
      name: {
        id: 'nazev',
        header: t('destinationSelector.columns.name'),
        value: (item) => item.name,
        sortable: true,
      },
      code: {
        id: 'zkratka',
        header: t('destinationSelector.columns.code'),
        value: (item) => item.code,
        emptyDash: '—',
      },
    }),
  pickFilters: (t) => [
    selectorPresets.apiPickFilter({
      id: 'seasonId',
      placeholder: t('destinationSelector.filters.allSeasons'),
      queryKey: () => getGetVenuaPlannerDestinationSeasonOptionsQueryKey(undefined),
      queryFn: () => getVenuaPlannerDestinationSeasonOptions(undefined),
      invalidMessage: 'Destination season options: invalid API response',
    }),
  ],
  i18n: { namespace: 'planner' },
});

export const DestinationSelector = destinationSelector.Selector;
export const DestinationSelectorField = createAsyncSelectorField(DestinationSelector);
